import React, { useContext, useEffect, type FC, type PropsWithChildren } from 'react';

import type { Lucid } from '../../../types/types';
import AccessibleDescription from '../../accessible-description/accessible-description';
import Link, { type LinkProps } from '../../link/link';
import { CardContext } from '../card';

export type CardLinkProps = Omit<Lucid.Directus.Generated.Links, 'id'> &
  LinkProps & {
    /** The beacon event config. */
    beaconEvent?: Lucid.Beacon.Event | null;
    /** Link will span height and width of Card component */
    isFullCardLink?: boolean;
    index?: number;
    shouldLinkWholeCard?: boolean;
    /** Accessible description of link */
    accessibleDescription?: string;
  };

const CardLink: FC<PropsWithChildren<CardLinkProps>> = ({
  isFullCardLink = false,
  shouldLinkWholeCard = true,
  default_link_text,
  final_url,
  accessibleDescription,
  beaconEvent,
  children,
  className,
  ...rest
}) => {
  const { setHasLinkChild } = useContext(CardContext);

  useEffect(() => {
    if (setHasLinkChild && final_url) {
      setHasLinkChild(true);
    }
  }, [final_url, setHasLinkChild]);

  if (!final_url) return null;

  return (
    <>
      {isFullCardLink ? (
        <Link
          {...rest}
          aria-label={children ? accessibleDescription : undefined}
          beaconEvent={beaconEvent}
          css={theme => ({
            label: 'cardLink',
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            '&::before': {
              content: '""',
              position: 'absolute',
              height: '100%',
              width: '100%',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: theme.zIndices.z100,
            },
          })}
          // eslint-disable-next-line @stylistic/jsx-sort-props
          className={className}
          data-card-element="link"
          href={final_url}
          variant="cardOutlineLink"
        >
          {(children ?? (accessibleDescription || default_link_text)) ? (
            <AccessibleDescription isForScreenReaderOnly SRText={accessibleDescription || default_link_text} />
          ) : (
            <></>
          )}
        </Link>
      ) : (
        <Link
          aria-label={accessibleDescription}
          {...rest}
          css={theme => [
            {
              zIndex: theme.zIndices.z500,
              display: 'block',
            },
            shouldLinkWholeCard && {
              '&::before': {
                content: '""',
                position: 'absolute',
                height: '100%',
                width: '100%',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: theme.zIndices.z100,
                borderStyle: 'none',
              },
            },
          ]}
          // eslint-disable-next-line @stylistic/jsx-sort-props
          className={className}
          href={final_url}
          iconAnimation="navigational"
          variant={shouldLinkWholeCard ? 'cardOutlineLink' : 'primary'}
        >
          {children ?? default_link_text}
        </Link>
      )}
    </>
  );
};

export { CardLink };
export default CardLink;
